import { useEffect, useState, useSyncExternalStore, type ReactNode } from 'react';
import { X, Edit2, Trash2, Clock, MapPin, Bell } from '../../icons';
import { EventDetailAttendeePanel } from './EventDetailAttendeePanel';
import {
  TYPE_COLOR_MAP, setRsvp, listReminders, createReminder, deleteReminder,
  type CalendarEventDto, type CalendarEventType, type RsvpStatus,
  type ReminderDto, type ReminderMethod,
} from '../../api/calendar';
import type { CalendarDto } from '../../api/calendar';
import { AttachmentList } from '../../components/Attachment';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { useEscapeClose } from '../../hooks/useEscapeClose';
import { tCalendar, getCalendarLang, subscribeCalendarLang } from '../../lib/calendar-i18n';
import { TypeIcon } from './calendarTypeMeta';
import { ReservationBadge } from './ReservationBadge';
import { EventLocationMap } from './EventLocationMap';
import { Avatar } from '../../components/shared/Avatar';
import { loadOrgPickerTree } from '../../lib/org-picker-tree';
import { useAuthStore } from '../../store/authStore';
import { indexPersonNames, needsOrgTree, attendeeName } from './lib/attendeeNames';
import { calendarLabelOf } from './lib/calendarLabel';
import { EventTravelTime } from './EventTravelTime';
import { EventMapToggle } from './EventMapToggle';
import { EventExternalAttendees } from './EventExternalAttendees';
import './EventDetailExtras.css';
import { DateTimePicker } from './DateTimePicker';
import { eventSpanLabel } from './lib/eventSpanLabel';
import { minutesFromAt, minutesFromCustom, reminderLabel, toPickerValue, type ReminderPick, type ReminderUnit } from './reminder-choice';

interface EventDetailModalProps {
  event: CalendarEventDto;
  isCreator: boolean;
  onClose: () => void;
  onDelete: () => void;
  onUpdated: () => void;  // PATCH/RSVP 후 refresh trigger.
  onEdit?: () => void;    // T-cal27 — 수정 버튼 클릭 → CalendarPage 가 EditModal 오픈.
  myUserId?: string | null;  // 그룹 G — 본인이 참석자면 RSVP 버튼 노출.
  isLeaving?: boolean;
  /**
   * 사이드바가 이미 들고 있는 캘린더 목록(2026-10-13 · 옛 가지 59edfc68c).
   * 주면 머리에 「캘린더(팀) → 유형」 한 줄을 그린다. 안 주면 종전 그대로(유형 배지만).
   */
  calendars?: ReadonlyArray<CalendarDto>;
  /**
   * 알림을 더하거나 지운 «뒤» 최신 목록을 알린다(2026-10-13 · 옛 가지 a2aa18e96).
   * CalendarPage 가 목록 캐시의 `reminders` 를 바로 고쳐 **일간 표가 새로고침 없이 따라온다.**
   * 옵셔널 — 안 주면 종전 그대로. 처음 열 때의 조회는 알리지 않는다(바뀐 것이 없다).
   */
  onRemindersChange?: (eventId: string, reminders: ReminderDto[]) => void;
  /**
   * 참석 여부를 바꾼 «뒤» 알린다(2026-10-19 사용자 지시: 골라도 상세 창이 꺼지지 않게).
   * 주면 창은 그대로 두고 이것만 부른다 — 창 안의 상태는 모달이 스스로 고쳐 그린다.
   * 안 주면 종전 그대로 `onUpdated`(갱신 후 닫힘).
   */
  onRsvpChange?: (eventId: string, status: RsvpStatus) => void;
}

/**
 * EventDetailModal (P3-CAL-FE-3) — 이벤트 상세 표시 + 작성자만 수정/삭제.
 *
 * 본 사이클은 표시 + 삭제만. 인라인 수정 (rename / 시간 변경) 은 후속 (별도 EventEditModal).
 * 작성자가 아니면 read-only.
 *
 * isLeaving: useModalLeave hook 의 unmount transition 단계 (CSS --leaving 토글).
 */
/** 2단일 때만 감싸는 칸 — 1단이면 감싸지 않아 DOM 이 종전과 같다(2026-10-19). */
function SplitPart({ on, className, children }: { on: boolean; className: string; children: ReactNode }) {
  return on ? <div className={className}>{children}</div> : <>{children}</>;
}

export function EventDetailModal({ event, isCreator, onClose, onDelete, onUpdated, onEdit, myUserId, isLeaving = false, calendars, onRemindersChange, onRsvpChange }: EventDetailModalProps) {
  useSyncExternalStore(subscribeCalendarLang, getCalendarLang, getCalendarLang);
  const trapRef = useFocusTrap<HTMLDivElement>(true);
  const [rsvpBusy, setRsvpBusy] = useState(false);
  const [rsvpErr, setRsvpErr] = useState<string | null>(null);
  /** 창을 연 채 고른 내 응답 — 받은 `event` 는 열 때의 사본이라 여기서 덮어 그린다. 다른 일정·회차로 바뀌면 비운다. */
  const [rsvpOverride, setRsvpOverride] = useState<{ key: string; status: RsvpStatus } | null>(null);
  const rsvpKey = `${event.id}@${event.occurrenceAt ?? ''}`;

  // 알림 — 본인 이벤트일 때 로드/관리. occurrence(반복) 가 아닌 master 기준.
  const [reminders, setReminders] = useState<ReminderDto[]>([]);
  const [reminderBusy, setReminderBusy] = useState(false);
  /**
   * 고른 알림 — 미리 정한 분('0'·'5'…), 'custom'(숫자 × 단위), 'at'(날짜·시간 · 단일 일정만).
   * ⭐ 2026-10-16 사용자가 고른 «방법 1» — 무엇을 골라도 서버에는 «시작 몇 분 전»으로 바꿔 보낸다(reminder-choice.ts).
   */
  const [reminderChoice, setReminderChoice] = useState('10');
  const [customAmount, setCustomAmount] = useState('15');
  const [customUnit, setCustomUnit] = useState<ReminderUnit>('min');
  const [reminderAt, setReminderAt] = useState(() => toPickerValue(new Date(new Date(event.startAt).getTime() - 60 * 60 * 1000)));
  /** 반복 일정이면 「날짜·시간 지정」이 성립하지 않는다(회차마다 그만큼 전이 된다) — 그 선택지를 숨긴다. */
  const isRecurring = !!event.recurrenceRule || !!event.occurrenceAt;
  const [reminderMethod, setReminderMethod] = useState<ReminderMethod>('popup');
  const [reminderErr, setReminderErr] = useState<string | null>(null);
  /** 「+ 알림 추가」를 눌러 고르는 줄을 펼쳤나 — 추가되면 다시 접는다(6100 과 같다). */
  const [addingReminder, setAddingReminder] = useState(false);

  async function loadReminders(notify = false) {
    try {
      const list = await listReminders(event.id);
      setReminders(list);
      if (notify) onRemindersChange?.(event.id, list);
    } catch { setReminders([]); }
  }
  useEffect(() => {
    // 본인이 보거나 참석한 이벤트만 reminder 조회(백엔드가 가시성 게이트).
    void loadReminders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event.id]);

  async function handleAddReminder() {
    if (reminderBusy) return;
    // 고른 것을 «시작 몇 분 전»으로 — 못 바꾸면(범위 밖·지난 시각 등) 까닭을 말하고 보내지 않는다.
    const pick: ReminderPick = reminderChoice === 'custom'
      ? minutesFromCustom(customAmount, customUnit)
      : reminderChoice === 'at'
        ? minutesFromAt(new Date(reminderAt), new Date(event.startAt), new Date())
        : { ok: true, minutes: Number(reminderChoice) };
    if (!pick.ok) {
      setReminderErr(tCalendar(`calendar.reminder.err_pick_${pick.reason}` as never));
      return;
    }
    setReminderBusy(true);
    setReminderErr(null);
    try {
      await createReminder(event.id, { method: reminderMethod, minutesBefore: pick.minutes });
      await loadReminders(true);
      setAddingReminder(false);
    } catch (err) {
      /*
        ★★ 서버는 «이미» 까닭을 말한다 — 같은 설정을 또 넣으면 409 `REMINDER_DUPLICATE` 다
           (`apps/chat-server/src/modules/calendar/reminders.routes.ts` · P2002 → 409).
           종전에는 `catch { }` 가 오류를 통째로 버려 «언제나» 「알림 변경 실패」였다
           (2026-09-09 사용자 리포트 · 2026-09-28 고침).
        ⚠ code 를 꺼내는 길은 «이 레포의 관용구»를 그대로 쓴다 — 같은 폴더 `CalendarSidebar.tsx`
          가 `CALENDAR_NAME_DUPLICATE` 를 이 꼴로 가른다. 다른 길을 만들면 같은 뜻이 두 벌이 된다.
        ⛔ 서버가 보내는 한국어 message 를 그대로 쓰지 않는다 — 서버 문구는 «한국어 하나»라
          en·ja 화면이 한국어로 나온다.
        ★ 그 밖의 실패는 «예전 그대로» 일반 문구다 — 이것이 대조군이고 시험이 함께 잠근다.
        ⚠ 삭제(`handleDeleteReminder`)는 이 갈래가 «필요 없다» — 그 라우트가 낼 수 있는 것은
          404 `REMINDER_NOT_FOUND` 뿐이고 409 는 POST 에만 있다(실측 2026-09-28).
      */
      const code = (err as { response?: { data?: { code?: string } } })?.response?.data?.code ?? '';
      setReminderErr(
        code === 'REMINDER_DUPLICATE'
          ? tCalendar('calendar.reminder.err_duplicate')
          : tCalendar('calendar.reminder.err'),
      );
    }
    finally { setReminderBusy(false); }
  }
  async function handleDeleteReminder(id: string) {
    setReminderBusy(true);
    try { await deleteReminder(event.id, id); await loadReminders(true); }
    catch { setReminderErr(tCalendar('calendar.reminder.err')); }
    finally { setReminderBusy(false); }
  }
  // 칩 글자(reminderLabel)는 reminder-choice.ts — 「1일 3시간 30분 전」처럼 섞인 값도 읽힌다.

  // 본인이 참석자인지 + 현재 status.
  // 창을 연 채 응답을 바꿨으면 그 값으로 덮어 그린다(내 줄만) — 단추와 명단이 같은 말을 하게.
  const myOverride = rsvpOverride && rsvpOverride.key === rsvpKey ? rsvpOverride.status : null;
  const shownAttendees = myOverride && myUserId != null
    ? event.attendees.map((a) => (a.userId === myUserId ? { ...a, status: myOverride } : a))
    : event.attendees;
  const myAttendee = myUserId != null ? shownAttendees.find((a) => a.userId === myUserId) : undefined;
  /** 내가 이 일정을 만든 사람인가 — 그러면 참석 여부 고르개를 안 띄운다(만든 사람은 늘 참석). */
  const isCreatorAttendee = myUserId != null && event.creatorId === myUserId;

  /*
    참석자 «이름» — DTO 가 userId 만 줘서 종전에는 「#5 (ACCEPTED)」라고 그리고 있었다.
    ⚠ 조직도는 3+N 호출이라, 작성자·나 말고 «모르는 사람이 있을 때만» 읽는다
      (참석자가 나 혼자인 일정은 호출 0). 자세한 까닭·만료 조건은 `lib/attendeeNames.ts` 머리말.
  */
  const myName = useAuthStore((s) => s.user?.name ?? null);
  const [nameIndex, setNameIndex] = useState<ReadonlyMap<string, string> | null>(null);
  const [namesLoading, setNamesLoading] = useState(false);
  const nameSources = { creator: event.creator, myUserId, myName, index: nameIndex, loading: namesLoading };

  useEffect(() => {
    if (nameIndex !== null) return;
    if (!needsOrgTree(event.attendees, { creator: event.creator, myUserId })) return;
    let cancelled = false;
    setNamesLoading(true);
    void loadOrgPickerTree()
      .then((tree) => { if (!cancelled) setNameIndex(indexPersonNames(tree)); })
      .catch(() => { /* 못 읽어도 화면은 선다 — 「알 수 없는 사용자」로 그린다 */ })
      .finally(() => { if (!cancelled) setNamesLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event.id]);

  async function handleRsvp(status: RsvpStatus) {
    if (rsvpBusy) return;
    setRsvpBusy(true);
    setRsvpErr(null);
    try {
      // ⭐ 2026-10-15 — 반복 회차는 «그 회차에만» 답한다(회차별 참석자·응답). 단일 일정은 종전 그대로.
      //   occurrenceAt 이 비면 `setRsvp` 가 쿼리를 안 붙인다(단일 일정과 같은 요청).
      await setRsvp(event.id, status, event.recurrenceRule ? event.occurrenceAt : undefined);
      if (onRsvpChange) {
        // 창은 그대로 — 고른 단추·명단을 바로 바꿔 그리고, 달력 쪽은 부모가 다시 읽는다.
        setRsvpOverride({ key: rsvpKey, status });
        onRsvpChange(event.id, status);
      } else {
        onUpdated();  // CalendarPage refresh → 모달 닫힘(최신 상태 반영).
      }
    } catch {
      setRsvpErr(tCalendar('calendar.rsvp.err'));
    } finally {
      setRsvpBusy(false);
    }
  }

  useEscapeClose(onClose);

  const meta = TYPE_COLOR_MAP[event.type as CalendarEventType] ?? { label: event.type, color: 'var(--text-muted)' };
  // 소속 캘린더 — 순서는 「캘린더(팀) → 유형」(2026-09-08 사용자 결정 · 옛 가지 569a59fa0: 팀 안의 유형이므로 팀이 먼저).
  const ownCalendar = calendars && event.calendarId ? calendars.find((c) => c.id === event.calendarId) : undefined;
  const typeBadge = (
    <span className="calendar-detail-type" style={{ background: meta.color }}>
      <TypeIcon type={event.type} size={12} /> {meta.label}
    </span>
  );
  const start = new Date(event.startAt);
  const end = new Date(event.endAt);

  // Cycle 55 — getDateLocale() 강제(lang-aware) · 긴 월/요일 옵션은 lib/eventSpanLabel.ts 로 옮겼다(2026-10-19).
  // ⭐ 2026-10-19 — 여러 날 일정이면 종료 «날짜»까지 보인다(사용자 지적). 하루 일정은 종전 글자 그대로(lib/eventSpanLabel.ts).
  const span = eventSpanLabel(start, end, !!event.isAllDay);

  /*
    ⭐ 2026-10-19 — 2단(사용자가 시안 D' 로 고름): 왼쪽 = 일정 정보, 오른쪽 = 내 참석 여부·알림(위 고정) + 참석자 명단.
      오른쪽에 «참석자 명단»이나 «내 참석 여부»가 있을 때만 연다. 둘 다 없으면(작성자 혼자인 일정 등)
      알림 한 줄뿐이라 종전 1단 그대로 둔다(사용자 결정 — 빈 칸이 휑하다).
      작성자가 «유일한» 참석자면 명단을 그리지 않는 규칙은 2026-10-16 그대로다.
  */
  const showAttendees = event.attendees.length > 0 && !(event.attendees.length === 1 && event.attendees[0]!.userId === event.creatorId);
  // ⭐ 2026-10-19 (사용자 지시) — 다른 사람을 초대한 일정이면 «작성자도» 참석 여부를 고른다.
  //   2026-10-14 의 「만든 사람은 고르지 않는다」는 «아무도 초대 안 했거나 자기만 고른» 일정(작성자 혼자)에만 남는다.
  //   서버는 막지 않는다 — RSVP 는 참석자 본인이면 되고, 참석자를 고쳐도 남는 사람의 응답은 지킨다(buildAttendeeRows).
  const othersInvited = event.attendees.some((a) => a.userId !== event.creatorId);
  const showRsvp = !!myAttendee && (!isCreatorAttendee || othersInvited);
  const split = showAttendees || showRsvp;

  return (
    <div
      className={`calendar-modal-backdrop${isLeaving ? ' calendar-modal-backdrop--leaving' : ''}`}
      onClick={(e) => { if (e.target === e.currentTarget && !isLeaving) onClose(); }}
      role="dialog"
      aria-modal="true"
      aria-label={tCalendar('calendar.detail.aria')}
      aria-hidden={isLeaving || undefined}
    >
      <div ref={trapRef} className={`calendar-modal calendar-modal--detail${split ? ' calendar-modal--detail-split' : ''}${isLeaving ? ' calendar-modal--leaving' : ''}`}>
        <header className="calendar-modal-header">
          <div className="calendar-detail-header-block">
            {calendars ? (
              <span className="calendar-detail-type-line">
                <span className="calendar-detail-calendar" data-testid="detail-calendar-label">
                  <span
                    className="calendar-detail-calendar-dot"
                    style={ownCalendar?.color ? { background: ownCalendar.color } : undefined}
                    aria-hidden
                  />
                  {calendarLabelOf(event, calendars)}
                </span>
                {typeBadge}
              </span>
            ) : typeBadge}
            <h2 className="calendar-detail-title"><ReservationBadge event={event} variant="text" />{event.title}</h2>
          </div>
          <button type="button" className="calendar-modal-close" onClick={onClose} aria-label={tCalendar('calendar.btn.close')}>
            <X size={18} />
          </button>
        </header>

        <div className="calendar-modal-body calendar-detail-body">
          <SplitPart on={split} className="calendar-detail-split">
          <SplitPart on={split} className="calendar-detail-main">
          <div className="calendar-detail-row">
            <Clock size={14} />
            <div>
              {span.lines.map((line) => <div key={line}>{line}</div>)}
              {span.secondary && <div className="calendar-detail-row-secondary">{span.secondary}</div>}
            </div>
          </div>

          {/* 「외부」 일정 — 지도·이동 시간은 「지도」 단추를 눌렀을 때만(2026-10-30 · 사용자가 시안 B 로 고름).
              일정이 바뀌면 key 로 새로 그려 «닫힘»에서 다시 시작한다. 아래 종전 줄은 외부가 아닐 때만 탄다. */}
          {event.location && event.type === 'external' && (
            <div className="calendar-detail-row">
              <MapPin size={14} />
              <EventMapToggle key={`${event.id}@${event.occurrenceAt ?? ''}`} eventId={event.id} occurrenceAt={event.occurrenceAt} location={event.location} />
            </div>
          )}

          {event.location && event.type !== 'external' && (
            <div className="calendar-detail-row">
              <MapPin size={14} />
              {/* 장소 글자 «아래»에 지도를 붙인다(2026-09-29 사용자가 시안 「안 A」로 고름).
                  ⭐ 찾을 수 없는 장소(「3층 회의실」 등)면 EventLocationMap 이 null 을 돌려
                    이 줄은 종전과 «똑같이» 글자만 남는다 — 빈 지도도 자리도 안 남긴다. */}
              <div className="calendar-detail-location">
                {event.location}
                {/* 「외부」 일정은 내 사업장에서의 «실제 길» + 자동차·도보(2026-10-13). 셀 수 없으면
                    (키 없음·카카오 실패·장소 못 찾음) 종전 장소 지도로 조용히 물러선다. 다른 유형은 종전 그대로. */}
                {event.type === 'external'
                  ? <EventTravelTime eventId={event.id} occurrenceAt={event.occurrenceAt} fallback={<EventLocationMap location={event.location} />} />
                  : <EventLocationMap location={event.location} />}
              </div>
            </div>
          )}

          {/* 작성자 — ⭐ 2026-10-19 (사용자 지시) 캘린더와 상관없이 «늘» 보인다(기본·개인 캘린더 일정도 누구 것인지 알아야 한다).
              종전에는 팀 캘린더 일정에서만 그렸다. 월간 칩의 작은 아바타(EventCreatorAvatar)는 종전대로 팀 일정만이다. */}
          {event.creator && (
            <div className="calendar-detail-row calendar-detail-creator">
              <span title={event.creator.name}>
                <Avatar userId={Number(event.creator.id)} name={event.creator.name} src={event.creator.avatarUrl} size="sm" />
              </span>
              <div>{tCalendar('calendar.detail.created_by', { name: event.creator.name })}</div>
            </div>
          )}

          {/*
            ⭐ 2026-10-19 — 참석자 명단은 2단의 오른쪽 칸(EventDetailAttendeePanel)으로 옮겼다(시안 D').
              상태 글자는 종전처럼 사전을 거친다(2026-09-29 — 날것 (ACCEPTED) 금지). 작성자가 «유일한»
              참석자면 그리지 않는 규칙(2026-10-16)은 `showAttendees` 로 그대로 남는다.
          */}

          {event.description && (
            <div className="calendar-detail-description">
              {event.description}
            </div>
          )}

          {event.attachments.length > 0 && (
            <div className="calendar-detail-attachments">
              <AttachmentList attachments={event.attachments} />
            </div>
          )}

          {/* ⭐ 2026-11-02 (지시 57) — 외부 참석자(주소록) · 마지막 메일 상태 · 작성자만 「다시 보내기」. 없으면 아무것도 안 그린다. */}
          <EventExternalAttendees key={event.id} eventId={event.id} isCreator={isCreator} />
          </SplitPart>

          <SplitPart on={split} className="calendar-detail-side">
          {/* ⭐ 2026-10-19 (사용자 지시) — 오른쪽 칸 순서: 내 참석 여부 → 참석자 명단 → 알림(맨 아래). */}
          <SplitPart on={split && showRsvp} className="calendar-detail-side-mine">
          {/* 2026-10-14 — 만든 사람은 참석 여부를 고르지 않는다(언제나 참석). 참석자 명단에는 그대로 보인다.
              ⭐ 2026-10-19 — 단, 다른 사람을 초대했으면 작성자도 고른다(`showRsvp`). */}
          {showRsvp && myAttendee && (
            <div className="calendar-rsvp-section">
              {/* ⭐ 2026-10-19 (사용자가 시안 A 로 고름) — 「내 참석」 글자 오른쪽에 작은 묶음 단추 셋, 한 줄.
                  고른 단추가 곧 내 응답이라 종전 머리의 「· 참석」 글자는 뺐다. */}
              <div className="calendar-rsvp-heading" id="calendar-rsvp-label">{tCalendar('calendar.rsvp.mine')}</div>
              <div className="calendar-rsvp-buttons" role="group" aria-labelledby="calendar-rsvp-label">
                {/* ⭐ 2026-10-23 사용자 지시 — 「미정」 단추는 없다. 고르지 않은 상태가 곧 미정이라
                    참석 · 불참 둘만 두고, 켜진 단추를 다시 누르면 꺼지며 미정(TENTATIVE)으로 돌아간다.
                    (서버는 PENDING 을 받지 않으므로 «안 고름»은 TENTATIVE 로 보낸다 — 화면 표기는 둘 다 「미정」.) */}
                {(['ACCEPTED', 'DECLINED'] as const).map((s) => (
                  <button
                    key={s}
                    type="button"
                    className={`calendar-rsvp-btn ${myAttendee.status === s ? 'calendar-rsvp-btn--active' : ''}`}
                    aria-pressed={myAttendee.status === s}
                    onClick={() => void handleRsvp(myAttendee.status === s ? 'TENTATIVE' : s)}
                    disabled={rsvpBusy}
                  >
                    {tCalendar(`calendar.rsvp.${s.toLowerCase()}` as never)}
                  </button>
                ))}
              </div>
              {rsvpErr && <div className="calendar-modal-error" role="alert">{rsvpErr}</div>}
            </div>
          )}
          </SplitPart>
          {showAttendees && (
            <EventDetailAttendeePanel
              attendees={shownAttendees}
              creatorId={event.creatorId}
              myUserId={myUserId}
              nameOf={(id) => attendeeName(id, nameSources)}
            />
          )}
          <SplitPart on={split} className="calendar-detail-side-foot">
          {/*
            알림 — 본인 가시 이벤트(reminders 로드 성공 기준). master 기준 설정.
            ⭐ 2026-10-16 — 6100(feature/dev-kyeongeun) 화면 그대로(사용자 지시): 「아이콘 · 알림 · 값」 한 줄.
              설정된 알림은 칩(✕ 로 삭제), 끝에 「+ 알림 추가」 링크. 누르면 그 아래에 고르개 둘 + 「알림 추가」 · 「취소」가
              나왔다가 추가되면 접힌다. 고르개에 「5분 전」이 있다(6100 2026-09-09 사용자 요청).
          */}
          <div className="calendar-detail-row calendar-detail-row--labeled calendar-reminder-row">
            <Bell size={14} aria-hidden />
            <span className="calendar-detail-row-label">{tCalendar('calendar.reminder.heading')}</span>
            <div className="calendar-reminder-body">
              {/* 알림이 없고 추가 줄이 열려 있으면 이 줄은 비어 있으니 그리지 않는다(빈 공간 방지). */}
              {(reminders.length > 0 || !addingReminder) && (
              <div className="calendar-reminder-line">
                {reminders.map((r) => (
                  <span key={r.id} className="calendar-reminder-chip">
                    {reminderLabel(r.minutesBefore)} · {tCalendar(r.method === 'email' ? 'calendar.reminder.method_email' : 'calendar.reminder.method_popup')}
                    <button
                      type="button"
                      className="calendar-reminder-chip-del"
                      onClick={() => void handleDeleteReminder(r.id)}
                      disabled={reminderBusy}
                      aria-label={tCalendar('calendar.detail.btn_delete')}
                    >
                      <X size={11} aria-hidden />
                    </button>
                  </span>
                ))}
                {!addingReminder && (
                  <button
                    type="button"
                    className="calendar-reminder-addlink"
                    onClick={() => { setReminderErr(null); setAddingReminder(true); }}
                    disabled={reminderBusy}
                  >
                    + {tCalendar('calendar.reminder.add')}
                  </button>
                )}
              </div>
              )}
              {addingReminder && (
            <div className="calendar-reminder-add">
              <select value={reminderChoice} onChange={(e) => { setReminderChoice(e.target.value); setReminderErr(null); }} disabled={reminderBusy} aria-label={tCalendar('calendar.reminder.heading')}>
                <option value={0}>{tCalendar('calendar.reminder.at_time')}</option>
                <option value={5}>{tCalendar('calendar.reminder.min_before', { n: 5 })}</option>
                <option value={10}>{tCalendar('calendar.reminder.min_before', { n: 10 })}</option>
                <option value={30}>{tCalendar('calendar.reminder.min_before', { n: 30 })}</option>
                <option value={60}>{tCalendar('calendar.reminder.hour_before', { n: 1 })}</option>
                <option value={1440}>{tCalendar('calendar.reminder.day_before', { n: 1 })}</option>
                <option value="custom">{tCalendar('calendar.reminder.opt_custom')}</option>
                {!isRecurring && <option value="at">{tCalendar('calendar.reminder.opt_at')}</option>}
              </select>
              {reminderChoice === 'custom' && (
                <span className="calendar-reminder-custom">
                  <input
                    type="number"
                    inputMode="numeric"
                    min={1}
                    value={customAmount}
                    onChange={(e) => { setCustomAmount(e.target.value); setReminderErr(null); }}
                    disabled={reminderBusy}
                    aria-label={tCalendar('calendar.reminder.custom_amount')}
                  />
                  <select value={customUnit} onChange={(e) => { setCustomUnit(e.target.value as ReminderUnit); setReminderErr(null); }} disabled={reminderBusy} aria-label={tCalendar('calendar.reminder.custom_unit')}>
                    <option value="min">{tCalendar('calendar.reminder.unit_min')}</option>
                    <option value="hour">{tCalendar('calendar.reminder.unit_hour')}</option>
                    <option value="day">{tCalendar('calendar.reminder.unit_day')}</option>
                    <option value="week">{tCalendar('calendar.reminder.unit_week')}</option>
                  </select>
                  <span className="calendar-reminder-custom-suffix">{tCalendar('calendar.reminder.before_suffix')}</span>
                </span>
              )}
              {reminderChoice === 'at' && (
                <span className="calendar-reminder-at">
                  <DateTimePicker id="calendar-reminder-at" value={reminderAt} onChange={(v) => { setReminderAt(v); setReminderErr(null); }} />
                </span>
              )}
              <select value={reminderMethod} onChange={(e) => setReminderMethod(e.target.value as ReminderMethod)} disabled={reminderBusy}>
                <option value="popup">{tCalendar('calendar.reminder.method_popup')}</option>
                <option value="email">{tCalendar('calendar.reminder.method_email')}</option>
              </select>
              <button type="button" onClick={() => void handleAddReminder()} disabled={reminderBusy} className="calendar-reminder-addbtn">
                {tCalendar('calendar.reminder.add')}
              </button>
              <button type="button" className="calendar-reminder-cancel" onClick={() => setAddingReminder(false)} disabled={reminderBusy}>
                {tCalendar('calendar.btn.cancel')}
              </button>
            </div>
              )}
              {reminderErr && <div className="calendar-modal-error" role="alert">{reminderErr}</div>}
            </div>
          </div>
          </SplitPart>
          </SplitPart>
          </SplitPart>

          <footer className="calendar-modal-footer">
            {/* 작성자 아님 안내 — 6100 그대로(2026-10-16): 구분선 아래(푸터 왼쪽), 배경 없이 글만. */}
            {!isCreator && (
              <span className="calendar-detail-readonly calendar-detail-readonly--footer" role="note">
                {tCalendar('calendar.detail.readonly_note')}
              </span>
            )}
            <button type="button" onClick={onClose} autoFocus>{tCalendar('calendar.btn.close')}</button>
            {isCreator && (
              <>
                <button type="button" onClick={onEdit}>
                  <Edit2 size={14} /> {tCalendar('calendar.detail.btn_edit')}
                </button>
                <button
                  type="button"
                  onClick={onDelete}
                  className="calendar-detail-delete"
                >
                  <Trash2 size={14} /> {tCalendar('calendar.detail.btn_delete')}
                </button>
              </>
            )}
          </footer>
        </div>
      </div>
    </div>
  );
}
